import {
  MapArtifactSchema,
  ProductErrorSchema,
  SUPPORTED_ARTIFACT_VERSIONS,
  artifactHashPayload,
  type CatalogEntry,
  type MapVersionView,
} from "@urlcade/contracts";
import type { LoadedMap } from "./maps";
import { ProductApi } from "./product-api";
import { endpoint } from "./config";

const maxPrivateAssetBytes = 16 * 1024 * 1024;
const maxPrivateMapBytes = 64_000_000;

export async function loadAuthorizedMapVersion(api: ProductApi, version: MapVersionView, signal?: AbortSignal): Promise<LoadedMap> {
  return loadAuthorizedVersion(api, version.id, version, signal);
}

export async function loadAuthorizedRoomMapVersion(
  api: ProductApi,
  versionId: string,
  expected: Pick<MapVersionView, "mapId" | "artifactHash" | "mode"> & Partial<Pick<MapVersionView, "compilerVersion" | "simulationVersion" | "protocolVersion" | "rulesHash">>,
  signal?: AbortSignal,
): Promise<LoadedMap> {
  return loadAuthorizedVersion(api, versionId, expected, signal);
}

async function loadAuthorizedVersion(
  api: ProductApi,
  versionId: string,
  expected: Pick<MapVersionView, "mapId" | "artifactHash" | "mode"> & Partial<Pick<MapVersionView, "compilerVersion" | "simulationVersion" | "protocolVersion" | "rulesHash">>,
  signal?: AbortSignal,
): Promise<LoadedMap> {
  throwIfAborted(signal);
  const response = await api.getMapManifest(versionId, signal);
  const artifact = MapArtifactSchema.parse(response.artifact);
  if (artifact.artifactHash !== expected.artifactHash || artifact.mapId !== expected.mapId || artifact.setup.mode !== expected.mode ||
    (expected.compilerVersion && artifact.compilerVersion !== expected.compilerVersion) ||
    (expected.simulationVersion && artifact.simulationVersion !== expected.simulationVersion) || (expected.protocolVersion && artifact.protocolVersion !== expected.protocolVersion) ||
    (artifact.schemaVersion >= 3 && expected.rulesHash && artifact.rulesHash !== expected.rulesHash)) {
    throw new Error("VERSION_MISMATCH");
  }
  if (!(SUPPORTED_ARTIFACT_VERSIONS as readonly string[]).includes(artifact.simulationVersion) || !(SUPPORTED_ARTIFACT_VERSIONS as readonly string[]).includes(artifact.protocolVersion)) throw new Error("VERSION_MISMATCH");
  if (await sha256(new TextEncoder().encode(canonical(artifactHashPayload(artifact)))) !== artifact.artifactHash) throw new Error("ASSET_DIGEST_MISMATCH");

  throwIfAborted(signal);
  const access = await api.getAssetAccess(versionId, signal);
  const expectedIds = artifact.assets.map((asset) => asset.id);
  const byId = new Map(access.assets.map((asset) => [asset.assetId, asset]));
  if (byId.size !== expectedIds.length || expectedIds.some((assetId) => !byId.has(assetId))) throw new Error("ASSET_MISSING");

  throwIfAborted(signal);
  const token = await api.bearerToken();
  throwIfAborted(signal);
  const createdUrls: string[] = [];
  const assetUrls = new Map<string, string>();
  let totalBytes = 0;
  try {
    for (const asset of artifact.assets) {
      throwIfAborted(signal);
      const ticket = byId.get(asset.id)!;
      if (ticket.digest !== asset.digest || ticket.bytes !== asset.bytes) throw new Error("ASSET_DIGEST_MISMATCH");
      const apiBase = new URL(endpoint("/"));
      const url = new URL(ticket.url, apiBase);
      if ((url.protocol !== "https:" && url.protocol !== "http:") || url.origin !== apiBase.origin ||
        [...url.searchParams].some(([key, value]) => /ticket/i.test(key) || value === ticket.ticket)) throw new Error("ASSET_MISSING");
      const responseAsset = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, "X-Asset-Ticket": ticket.ticket, Accept: "image/png,image/webp" },
        cache: "no-store",
        credentials: "omit",
        signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(15_000)]) : AbortSignal.timeout(15_000),
      });
      if (!responseAsset.ok) {
        if (responseAsset.status === 401 || responseAsset.status === 403) throw new Error("ACCESS_REVOKED");
        const body: unknown = await responseAsset.json().catch(() => null);
        const productError = ProductErrorSchema.safeParse(body);
        if (productError.success && ["QUOTA_EXCEEDED", "CONFLICT", "ASSET_MISSING", "VERSION_MISMATCH", "SOURCE_REMOVED"].includes(productError.data.code)) {
          throw new Error(productError.data.code);
        }
        if (responseAsset.status === 429) throw new Error("RATE_LIMITED");
        throw new Error("ASSET_MISSING");
      }
      const bytes = await responseAsset.arrayBuffer();
      totalBytes += bytes.byteLength;
      if (bytes.byteLength !== asset.bytes || bytes.byteLength > maxPrivateAssetBytes || totalBytes > maxPrivateMapBytes) throw new Error("ASSET_TOO_LARGE");
      if (await sha256(bytes) !== asset.digest) throw new Error("ASSET_DIGEST_MISMATCH");
      throwIfAborted(signal);
      const contentType = responseAsset.headers.get("content-type")?.split(";")[0]?.toLowerCase();
      if (contentType !== "image/png" && contentType !== "image/webp") throw new Error("ASSET_INVALID");
      const blobUrl = URL.createObjectURL(new Blob([bytes], { type: contentType }));
      createdUrls.push(blobUrl);
      assetUrls.set(asset.id, blobUrl);
    }
    throwIfAborted(signal);
  } catch (error) {
    createdUrls.forEach(URL.revokeObjectURL);
    throw error;
  }

  const manifestUrl = `/api/map-versions/${versionId}/manifest`;
  const entry: CatalogEntry = {
    mapId: artifact.mapId,
    artifactHash: artifact.artifactHash,
    title: artifact.title,
    mode: artifact.setup.mode,
    manifestUrl,
    simulationVersion: artifact.simulationVersion,
    protocolVersion: artifact.protocolVersion,
  };
  return {
    entry,
    artifact,
    manifestUrl,
    assetUrls,
    dispose: () => createdUrls.forEach(URL.revokeObjectURL),
  };
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (typeof value === "object" && value !== null) {
    const object = value as Record<string, unknown>;
    return `{${Object.keys(object).sort().map((key) => `${JSON.stringify(key)}:${canonical(object[key])}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException("Asset loading was cancelled.", "AbortError");
}

async function sha256(value: ArrayBuffer | Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error("CRYPTO_UNAVAILABLE");
  const data = value instanceof Uint8Array ? new Uint8Array(value).buffer : value;
  const hash = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(hash)].map((part) => part.toString(16).padStart(2, "0")).join("");
}
